import { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { Select, Button, Spin, Empty, message, Popconfirm, Tag } from "antd";
import { SafetyCertificateOutlined, UserAddOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  getPermittedUsersForCategory,
  addCategoryPermission,
  removeCategoryPermission,
  getAllUsers,
} from "../../api/adminAPI";

interface WidgetUser {
  userId: number;
  name: string;
  username: string;
  role: string;
  workplace?: string;
  isWithdrawnYN?: boolean;
  [key: string]: any;
}

interface Props {
  categoryId: number;
  categoryName?: string;
  // 권한 보유자 목록 표시 열 수 (기본 2열). 관리자 페이지 등에서 넓게 쓸 때 3열 등으로.
  columns?: number;
}

const ROLE_LABELS: Record<string, string> = {
  ADVISOR: "자문위원",
  SPEACIAL: "특별회원",
  REGULAR: "정회원",
  GENERAL: "일반회원",
  STUDENT: "학생회원",
  OTHER: "기타회원",
};

const ROLE_COLORS: Record<string, string> = {
  ADMIN_MAIN: "red",
  ADMIN_SUB: "volcano",
  ADVISOR: "geekblue",
  SPEACIAL: "purple",
  REGULAR: "blue",
  GENERAL: "default",
};

const roleLabel = (role: string) => ROLE_LABELS[role] ?? role ?? "-";
const initialOf = (name?: string) => (name || "?").trim().charAt(0).toUpperCase();

const CategoryPermissionWidget = ({ categoryId, categoryName, columns = 2 }: Props) => {
  const [allUsers, setAllUsers] = useState<WidgetUser[]>([]);
  const [permittedIds, setPermittedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!categoryId) return;
    setLoading(true);
    try {
      const [usersRes, permitted] = await Promise.all([
        getAllUsers(),
        getPermittedUsersForCategory(categoryId),
      ]);
      const userList: WidgetUser[] = Array.isArray(usersRes?.data) ? usersRes.data : [];
      setAllUsers(userList);
      setPermittedIds(Array.isArray(permitted) ? permitted : []);
    } catch (e) {
      console.error("권한 위젯 로드 실패:", e);
      message.error("권한 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    setSelectedUserId(null);
    load();
  }, [load]);

  const permittedIdSet = useMemo(() => new Set(permittedIds), [permittedIds]);

  const permittedUsers = useMemo(
    () => allUsers.filter((u) => permittedIdSet.has(u.userId)),
    [allUsers, permittedIdSet]
  );

  // 후보: 권한 없고, 탈퇴하지 않았으며, 관리자/서브관리자가 아닌 사용자
  const candidateUsers = useMemo(
    () =>
      allUsers.filter(
        (u) =>
          !permittedIdSet.has(u.userId) &&
          !u.isWithdrawnYN &&
          u.role !== "ADMIN_MAIN" &&
          u.role !== "ADMIN_SUB"
      ),
    [allUsers, permittedIdSet]
  );

  const handleAdd = async () => {
    if (selectedUserId == null) return;
    setAdding(true);
    try {
      await addCategoryPermission(selectedUserId, categoryId);
      message.success("권한을 추가했습니다.");
      setSelectedUserId(null);
      await load();
    } catch (e) {
      console.error(e);
      message.error("권한 추가에 실패했습니다.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId: number) => {
    setBusyUserId(userId);
    try {
      await removeCategoryPermission(userId, categoryId);
      message.success("권한을 제거했습니다.");
      await load();
    } catch (e) {
      console.error(e);
      message.error("권한 제거에 실패했습니다.");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <Widget $columns={columns}>
      <WidgetHeader>
        <HeaderTitle>
          <SafetyCertificateOutlined />
          <span>카테고리 권한</span>
        </HeaderTitle>
        {categoryName && <HeaderCategory title={categoryName}>{categoryName}</HeaderCategory>}
      </WidgetHeader>

      <WidgetBody>
        {loading ? (
          <Center>
            <Spin />
          </Center>
        ) : (
          <>
            <SectionLabel>
              권한 보유자 <CountPill>{permittedUsers.length}</CountPill>
            </SectionLabel>

            {permittedUsers.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="권한 보유자가 없습니다."
                style={{ margin: "16px 0" }}
              />
            ) : (
              <UserList $columns={columns}>
                {permittedUsers.map((u) => (
                  <UserRow key={u.userId}>
                    <Avatar>{initialOf(u.name)}</Avatar>
                    <UserMeta>
                      <UserName>
                        <NameText title={u.name}>{u.name}</NameText>
                      </UserName>
                      {u.workplace && <Workplace title={u.workplace}>{u.workplace}</Workplace>}
                      <Tag color={ROLE_COLORS[u.role] ?? "default"} style={{ marginRight: 0 }}>
                        {roleLabel(u.role)}
                      </Tag>
                    </UserMeta>
                    <Popconfirm
                      title="권한 제거"
                      description={`${u.name} 님의 권한을 제거할까요?`}
                      okText="제거"
                      cancelText="취소"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleRemove(u.userId)}
                    >
                      <RemoveBtn type="text" size="small" danger loading={busyUserId === u.userId} icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </UserRow>
                ))}
              </UserList>
            )}

            <Divider />

            <SectionLabel>권한 추가</SectionLabel>
            <AddRow>
              <Select
                showSearch
                allowClear
                value={selectedUserId ?? undefined}
                placeholder="사용자 검색 (이름/아이디/소속)"
                style={{ flex: 1, minWidth: 0 }}
                onChange={(v) => setSelectedUserId(v ?? null)}
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
                options={candidateUsers.map((u) => ({
                  value: u.userId,
                  label: `${u.name} (@${u.username})${u.workplace ? ` · ${u.workplace}` : ""} · ${roleLabel(u.role)}`,
                }))}
                notFoundContent="추가할 사용자가 없습니다."
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                disabled={selectedUserId == null}
                loading={adding}
                onClick={handleAdd}
              >
                추가
              </Button>
            </AddRow>
          </>
        )}
      </WidgetBody>
    </Widget>
  );
};

export default CategoryPermissionWidget;

/* ============================ styled ============================ */

// 한 열당 폭(내부 padding/gap 포함 근사) 기준으로 위젯 전체 폭을 열 수에 비례시킨다.
const COLUMN_WIDTH = 236;
const COLUMN_GAP = 10;
const widgetWidth = (columns: number) =>
  columns * COLUMN_WIDTH + (columns - 1) * COLUMN_GAP + 32; // 32 = 좌우 padding

const Widget = styled.div<{ $columns: number }>`
  width: ${({ $columns }) => widgetWidth($columns)}px;
  max-width: 100%;
  flex-shrink: 0;
  position: sticky;
  top: 180px;
  align-self: flex-start;
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
`;

const WidgetHeader = styled.div`
  flex-shrink: 0;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;

  .anticon {
    color: #2563eb;
  }
`;

const HeaderCategory = styled.div`
  margin-top: 4px;
  font-size: 0.8rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WidgetBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 16px 16px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 10px;
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 0.72rem;
  font-weight: 700;
`;

const UserList = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: 8px;
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  background: #fafcff;
`;

const Avatar = styled.div`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2563eb;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
`;

const UserMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
`;

const UserName = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  max-width: 100%;
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
`;

// 이름: 남는 공간에서 줄어들며 말줄임 처리 (아이디는 유지)
const NameText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;


const Workplace = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const RemoveBtn = styled(Button)`
  flex-shrink: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #eef2f7;
  margin: 16px 0;
`;

const AddRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
